import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import SectionHeader from "components/SectionPillPropsIcons";
import {
  vendorCustomerSideSelect2,
  getPostTypeById,
  getPostTypeTemplateById,
  getPresignedAssetUrls,
  resolvePostTypeVendorOrganizationId,
  type PostTypeSelect2Item,
  type PostTypeRecord,
  type PostTypeSlotEntity,
  type PostTypeTemplate,
} from "../../api/postTypeApiCalls";
import InteractiveSignPost, {
  getTemplateViewBoxDimensions,
  DEFAULT_VIEWBOX,
  type SlotBlueprint,
} from "../../components/InteractiveSignPost";
import ProductSelectionModal, { type LinkedProduct } from "../../components/ProductSelectionModal";
import type { StudioCustomizationFlowProps } from "components/ordercheckoutpop";
import CustomItemModal from "components/CustomItemModal";
import { getImageSrc, useVariantImageUrlMap } from "../../components/Tableimageshelpers";

/**
 * Post Type & Sign Configuration section.
 * Fetches post type options dynamically via select2 API.
 */
export type PanelModalType = "main" | "bottom" | "accessory" | "";

/** Line item shape for view mode: slot_id + product images + accessory flag to map to slots */
export type ViewModeLineItem = {
  slot_id?: string;
  is_accessory?: boolean;
  images?: Array<{ url: string; alt_text?: string; default?: boolean }>;
};

/** Payload sent with installation order so work order can show exact product image per slot */
export type InstallationPostPayload = {
  post_type_id: string;
  illustration: string;
  slots: Array<{ slot_id: string; product_id: string; variant_id: string; image: string }>;
};

/** End customer for whom the installation is placed — drives POST .../customer_side/datatable filters. Omit on pages that do not scope post types by customer. Pass null when no recipient yet (e.g. brokerage/office only). */
export type PostTypeOrderEndUserContext = {
  membershipId: string;
  licenseType: string;
  globalUserId?: string;
  organizationId?: string;
};

/** Line product shape used to hydrate slot UI in edit mode (work order). */
export type EditModeHydrationProduct = {
  slot_id?: string;
  slotKey?: string;
  is_accessory?: boolean;
  product_id: string;
  name?: string;
  sku?: string;
  variant_id?: string;
  images?: Array<{ url: string; alt_text?: string; default?: boolean }>;
  image_url?: string;
};

export interface PostTypeSignConfigurationProps {
  /** "add" = full UI (dropdown, Add Accessory, slot click). "view" = filled slots only, read-only. "edit" = all slots like add, post type locked. */
  type?: "add" | "view" | "edit";
  vendorId: string;
  token: string;
  /** "agent" | "office" | null — derived from selected agent/office */
  pickerUserType?: "agent" | "office" | null;
  /** The selected agent/office user ID (for product/inventory etc.) */
  pickerUserId?: string;
  /** End user ID for accessory-rules API only: agent id or teammember id (never office id). When only office is selected, leave undefined so Browse Catalog is not called. */
  pickerEndUserId?: string;
  /** Signage company ID (for product datatable API) */
  pickerSignageCompanyId?: string;
  /** Called when a product is selected for a slot — parent can add it to Product Selection table.
   *  slotKey = logical slot key (e.g. \"main_panel\"), slotId = backend _id/id for that slot (or empty string).
   */
  onSlotProductSelect?: (product: LinkedProduct, slotKey: string, slotId?: string) => void;
  /** Called when user tries to assign a product that is already assigned to another slot; parent can show error toast. Slot is left empty. */
  onSlotDuplicateProduct?: () => void;
  /** Total count of accessories (linked + new) for badge next to Add Accessory */
  accessoryCount?: number;
  /** Called when the user changes the selected post type — parent can e.g. clear Product Selection */
  onPostTypeChange?: (postType: PostTypeSelect2Item | null) => void;
  /** View mode: post type id to load (from installation order installation_post.post_type_id) */
  viewModePostTypeId?: string;
  /** View mode: vendor id for fetching post type (e.g. pickerSignageCompanyId) */
  viewModeVendorId?: string;
  /** View mode: line items with images and is_accessory to map to slots */
  viewModeLineItems?: ViewModeLineItem[];
  /** View mode: saved installation_post from order — slot images use these per slot_id */
  viewModeInstallationPost?: InstallationPostPayload | null;
  /** Edit mode: existing slot-assigned products from parent — hydrates slot UI on load */
  editModeHydrationProducts?: EditModeHydrationProduct[];
  /** Called when installation_post payload is ready (add mode) so parent can send it with the order */
  onInstallationPostChange?: (payload: InstallationPostPayload | null) => void;
  /** Add mode only: called with { clearSlot } so parent can clear a slot when product is removed from Product Selection */
  onClearSlotReady?: (api: PostTypeSignConfigurationRef) => void;
  /** Called when user removes a slot (e.g. trash on rider) so parent can remove that slot's product from selected products */
  onSlotRemoved?: (slotKey: string, slotId?: string) => void;
  /** Called when user adds a custom item via Custom Item modal */
  onCustomItemAdd?: (data: { item_name: string; custom_info: string; price: string; quantity: string; taxable: boolean; length: string; height: string; width: string; weight: string }) => void | Promise<void>;
  /** Passed through to ProductSelectionModal for Studio customization-context flow */
  studioCustomization?: StudioCustomizationFlowProps;
  /** Product IDs already present in parent Product Selection (slot + accessory) to block duplicate slot preview updates */
  selectedProductIds?: string[];
  /**
   * Selected installation lines (product_id + variant_id + quantity) for Avl Qty consumption
   * inside ProductSelectionModal (inventory + Shop Signs).
   */
  selectedStockLines?: import("components/ProductSelectionModal").SelectedStockLine[];
  /**
   * Edit mode: original order line quantities so Avl Qty returns stock when a line is removed.
   */
  baselineStockLines?: import("components/ProductSelectionModal").SelectedStockLine[];
  /**
   * When set from Add Installation: `null` = no end-customer yet (do not load post types); `{ membershipId, licenseType }` = filter dropdown.
   * When omitted (undefined), post types load without membership_id/license_type (legacy behavior).
   */
  postTypeOrderContext?: PostTypeOrderEndUserContext | null;
}

export type PostTypeSignConfigurationRef = {
  clearSlot: (slotKey: string) => void;
  /** Clear whichever slot currently holds this product (edit work order rows without slotKey). */
  clearSlotByProductId: (productId: string) => void;
  /** Returns validation errors for visible slots without selection. Empty array if valid. */
  validateInstallationPost: () => string[];
  /** After Studio batch restore: patch slot product thumbnails from snapshot `installation_post.slots[].image`. */
  applyStudioSlotImagesFromPayload: (ip: InstallationPostPayload | null) => void;
};

/** Normalize slot id to string (API may return _id as string or ObjectId-like { $oid: "..." }) */
function normalizeSlotId(id: string | { $oid?: string } | undefined): string {
  if (id == null) return "";
  if (typeof id === "string") return id.trim();
  if (typeof id === "object" && id && "$oid" in id) return String((id as { $oid: string }).$oid).trim();
  return String(id).trim();
}

const PostTypeSignConfiguration: React.FC<PostTypeSignConfigurationProps> = ({
  type = "add",
  vendorId,
  token,
  pickerUserType,
  pickerUserId,
  pickerEndUserId,
  pickerSignageCompanyId,
  onSlotProductSelect,
  onSlotDuplicateProduct,
  accessoryCount = 0,
  onPostTypeChange,
  viewModePostTypeId,
  viewModeVendorId,
  viewModeLineItems = [],
  viewModeInstallationPost,
  editModeHydrationProducts = [],
  onInstallationPostChange,
  onClearSlotReady,
  onSlotRemoved,
  onCustomItemAdd,
  studioCustomization,
  selectedProductIds = [],
  selectedStockLines,
  baselineStockLines,
  postTypeOrderContext,
}) => {
  const isViewMode = type === "view";
  const isEditMode = type === "edit";
  const effectiveVendorId = useMemo(
    () =>
      resolvePostTypeVendorOrganizationId(
        isViewMode || isEditMode ? viewModeVendorId || vendorId : vendorId
      ),
    [isViewMode, isEditMode, viewModeVendorId, vendorId]
  );
  const effectiveSignageCompanyId = useMemo(
    () => resolvePostTypeVendorOrganizationId(pickerSignageCompanyId ?? vendorId),
    [pickerSignageCompanyId, vendorId]
  );

  // Debug: how view-mode data flows from parent (SignageInstallationWorkOrder) into this component
  // Dynamic post type options from API
  const [postTypeOptions, setPostTypeOptions] = useState<PostTypeSelect2Item[]>([]);
  const [postTypeLoading, setPostTypeLoading] = useState(false);
  const [postTypeSearch, setPostTypeSearch] = useState("");
  const [selectedPostType, setSelectedPostType] = useState<PostTypeSelect2Item | null>(null);

  const [postDropdownOpen, setPostDropdownOpen] = useState(false);
  const [panelModal, setPanelModal] = useState<{
    open: boolean;
    type: PanelModalType;
    slot?: PostTypeSlotEntity;
  }>({ open: false, type: "" });
  const [hoveredSlot, setHoveredSlot] = useState<string | null>(null);
  const [postTypeDetail, setPostTypeDetail] = useState<PostTypeRecord | null>(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Post type design templates (same as PostTypes.jsx) — for dynamic illustration SVG
  const [postTemplates, setPostTemplates] = useState<PostTypeTemplate[]>([]);
  const [templateImages, setTemplateImages] = useState<Record<string, { preview?: string; thumbnail?: string; svg?: string }>>({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  /** Accessory rules for the selected post type — fetched when Add Accessory is opened; used for Browse Catalog counts and brand/category ids */

  // Products assigned to each slot — keyed by slot key
  const [slotProducts, setSlotProducts] = useState<Record<string, LinkedProduct>>({});
  // Previous duplicate-check support for parent Product Selection IDs:
  // const selectedProductIdsSet = useMemo(
  //   () => new Set((selectedProductIds || []).filter(Boolean).map((id) => String(id))),
  //   [selectedProductIds]
  // );
  const [showCustomModal, setShowCustomModal] = useState(false);

  /** Controls which slots are visible (toggle slots can be hidden; always slots always true). */
  const [visibleSlots, setVisibleSlots] = useState<Record<string, boolean>>({});
  const visibleSlotsRef = useRef<Record<string, boolean>>({});
  visibleSlotsRef.current = visibleSlots;

  /** Avoid effect dependency on parent inline `onPostTypeChange` (new ref each render → max update depth). */
  const onPostTypeChangeRef = useRef(onPostTypeChange);
  onPostTypeChangeRef.current = onPostTypeChange;

  const clearSlot = useCallback((slotKey: string) => {
    setSlotProducts((prev) => {
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
  }, []);

  const clearSlotByProductId = useCallback((productId: string) => {
    const pid = String(productId || "").trim();
    if (!pid) return;
    setSlotProducts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [key, p] of Object.entries(prev)) {
        if (String(p?.product_id ?? "").trim() === pid) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  // Stable representation of viewModeLineItems for effect deps (avoids loop when parent passes new array ref every render)
  const viewModeSlotIdsKey = useMemo(() => {
    if (!isViewMode || !viewModeLineItems?.length) return "";
    const ids = (viewModeLineItems as ViewModeLineItem[])
      .filter((item) => !item.is_accessory && item.slot_id)
      .map((item) => String(item.slot_id))
      .sort();
    return ids.join(",");
  }, [isViewMode, viewModeLineItems]);

  // Initialize visibleSlots from backend slot metadata and existing selection.
  // In add mode: use display_mode/default_visible + local slotProducts.
  // In view mode: show ONLY slots that have a matching slot_id in viewModeLineItems (product-linked slots only).
  useEffect(() => {
    if (!postTypeDetail?.slots?.length) return;

    // VIEW MODE: show only slots that have a matching non-accessory line item.slot_id
    if (isViewMode) {
      const usedSlotIds = new Set(
        viewModeSlotIdsKey ? viewModeSlotIdsKey.split(",").filter(Boolean) : []
      );

      const next: Record<string, boolean> = {};
      for (const slot of postTypeDetail.slots) {
        const rawId = (slot as any)._id ?? (slot as any).id;
        const slotId = rawId != null ? String(rawId) : "";
        next[slot.key] = !!slotId && usedSlotIds.has(slotId);
      }
      setVisibleSlots((prev) => {
        const same = Object.keys(next).length === Object.keys(prev).length
          && Object.keys(next).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
      return;
    }

    // ADD MODE: keep existing behavior (display_mode/default_visible + local slotProducts)
    const prevVisible = visibleSlotsRef.current;
    const next: Record<string, boolean> = {};
    for (const slot of postTypeDetail.slots) {
      const hasExistingSelection = !!slotProducts[slot.key];
      const isAlways = (slot as any).display_mode === "always";
      const defaultVisible = !!(slot as any).default_visible;
      const isToggle = (slot as any).display_mode === "toggle";
      next[slot.key] =
        isAlways ||
        defaultVisible ||
        hasExistingSelection ||
        (isToggle && !!prevVisible[slot.key]); // preserve user-expanded toggle slots when no product yet
    }
    setVisibleSlots((prev) => {
      const merged = { ...prev, ...next };
      const same = Object.keys(merged).every((k) => merged[k] === prev[k]) && Object.keys(prev).every((k) => merged[k] === prev[k]);
      return same ? prev : merged;
    });
  }, [postTypeDetail?.slots, isViewMode, viewModeSlotIdsKey, slotProducts]);

  const handleAddSlot = useCallback((slotKey: string) => {
    setVisibleSlots((prev) => ({ ...prev, [slotKey]: true }));
  }, []);

  const slotMetaByKey = useMemo(() => {
    const map: Record<string, any> = {};
    for (const s of postTypeDetail?.slots ?? []) {
      map[s.key] = s;
    }
    return map;
  }, [postTypeDetail?.slots]);

  const handleRemoveSlot = useCallback((slotKey: string) => {
    // Rider/toggle slots are removable (hide slot); `main_panel` should clear selection
    // but remain visible so the user can re-pick a product.
    const meta = slotMetaByKey[slotKey];
    const displayMode = meta?.display_mode;
    const defaultVisible = !!meta?.default_visible;
    const keepVisible =
      slotKey === "main_panel" ||
      displayMode === "always" ||
      (defaultVisible && displayMode !== "toggle");

    setVisibleSlots((prev) => ({ ...prev, [slotKey]: !!keepVisible }));
    setSlotProducts((prev) => {
      const next = { ...prev };
      delete next[slotKey];
      return next;
    });
    const slotId = normalizeSlotId(meta?._id ?? (meta as { id?: string }).id);
    onSlotRemoved?.(slotKey, slotId);
  }, [onSlotRemoved, slotMetaByKey]);

  const isSlotVisible = useCallback(
    (slotKey: string) => !!visibleSlots[slotKey],
    [visibleSlots]
  );

  // Image URL resolution for slot product images
  const HEX24 = /^[a-f0-9]{24}$/i;
  const ABS_URL = /^https?:\/\//i;
  const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));

  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const product of Object.values(slotProducts)) {
      const id =
        product.design_id && product.preview_url
          ? product.preview_url
          : product?.images?.[0]?.url;
      if (isHexId(id)) ids.push(String(id).trim());
    }
    if (isViewMode && viewModeInstallationPost?.slots?.length) {
      for (const s of viewModeInstallationPost.slots) {
        if (isHexId(s.image)) ids.push(s.image.trim());
      }
    }
    return Array.from(new Set(ids));
  }, [slotProducts, isViewMode, viewModeInstallationPost?.slots]);

  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [{ product: { variants: [{ images: allImageIds.map(url => ({ url })) }] } }];
  }, [allImageIds]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  const resolveImageUrl = useCallback((product: LinkedProduct): string => {
    const raw =
      product.design_id && product.preview_url
        ? product.preview_url
        : product?.images?.[0]?.url;
    if (!raw) return "";
    if (ABS_URL.test(raw)) return raw;
    if (isHexId(raw)) {
      const map: Record<string, string> = (imageUrlMap as any) || {};
      return map[raw] || getImageSrc(raw, map) || "";
    }
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
  }, [imageUrlMap]);

  const applyStudioSlotImagesFromPayload = useCallback(
    (ip: InstallationPostPayload | null) => {
      if (!ip?.slots?.length || !postTypeDetail?.slots?.length) return;
      const slotIdToKey: Record<string, string> = {};
      for (const s of postTypeDetail.slots) {
        const id = normalizeSlotId(s._id ?? (s as { id?: string }).id);
        if (id) slotIdToKey[id] = s.key;
      }
      setSlotProducts((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const entry of ip.slots) {
          const slotKey = slotIdToKey[normalizeSlotId(entry.slot_id as string | { $oid?: string } | undefined)];
          if (!slotKey || !entry.image) continue;
          const prod = prev[slotKey];
          if (!prod?.product_id) continue;
          const entryProductId = String(entry.product_id ?? "").trim();
          const prodId = String(prod.product_id ?? "").trim();
          if (entryProductId && prodId && entryProductId !== prodId) continue;
          changed = true;
          next[slotKey] = {
            ...prod,
            images: [{ url: entry.image, default: true }],
          };
        }
        return changed ? next : prev;
      });
    },
    [postTypeDetail?.slots]
  );

  // View mode: build slotImageUrls from installation_post — match slot_id to post type slot _id, resolve image id to URL
  const slotImageUrlsFromInstallationPost = useMemo(() => {
    if (!isViewMode || !viewModeInstallationPost?.slots?.length || !postTypeDetail?.slots?.length) return null;
    const map: Record<string, string> = (imageUrlMap as any) || {};
    const urls: Record<string, string> = {};
    const slotIdToKey: Record<string, string> = {};
    for (const s of postTypeDetail.slots) {
      const id = normalizeSlotId(s._id ?? s.id);
      if (id) slotIdToKey[id] = s.key;
    }
    for (const entry of viewModeInstallationPost.slots) {
      const entrySlotId = normalizeSlotId(entry.slot_id as string | { $oid?: string } | undefined);
      const slotKey = slotIdToKey[entrySlotId];
      if (!slotKey || !entry.image) continue;
      const resolved = getImageSrc(entry.image, map) || (ABS_URL.test(entry.image) ? entry.image : "");
      if (resolved) urls[slotKey] = resolved;
    }
    return Object.keys(urls).length ? urls : null;
  }, [isViewMode, viewModeInstallationPost?.slots, postTypeDetail?.slots, imageUrlMap]);

  // slotImageUrls: in view mode with installation_post use slot-by-slot mapping only; otherwise use slotProducts.
  // Missing images: no fake thumbnail URL — InteractiveSignPost uses slotNoImageKeys for dashed "No image" state.
  const slotImageUrls = useMemo(() => {
    if (isViewMode && viewModeInstallationPost?.slots?.length) {
      return slotImageUrlsFromInstallationPost ?? {};
    }
    // Add/edit: always derive from live slotProducts — never stale installation_order.installation_post
    const urls: Record<string, string> = {};
    for (const [slotKey, product] of Object.entries(slotProducts)) {
      const url = resolveImageUrl(product) || "";
      if (url) urls[slotKey] = url;
    }
    return urls;
  }, [isViewMode, viewModeInstallationPost?.slots?.length, slotImageUrlsFromInstallationPost, slotProducts, resolveImageUrl]);

  const slotNoImageKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const [slotKey, product] of Object.entries(slotProducts)) {
      if (product?.product_id && !String(resolveImageUrl(product) || "").trim()) {
        keys.add(slotKey);
      }
    }
    if (isViewMode && viewModeInstallationPost?.slots?.length && postTypeDetail?.slots?.length) {
      const slotIdToKey: Record<string, string> = {};
      for (const s of postTypeDetail.slots) {
        const id = normalizeSlotId(s._id ?? (s as { id?: string }).id);
        if (id) slotIdToKey[id] = s.key;
      }
      const resolvedMap = slotImageUrlsFromInstallationPost;
      for (const entry of viewModeInstallationPost.slots) {
        const pid = String(entry.product_id || "").trim();
        if (!pid) continue;
        const sk = slotIdToKey[normalizeSlotId(entry.slot_id as string | { $oid?: string } | undefined)];
        if (!sk) continue;
        const u = resolvedMap?.[sk];
        if (!String(u || "").trim()) keys.add(sk);
      }
    }
    return Array.from(keys);
  }, [
    slotProducts,
    resolveImageUrl,
    isViewMode,
    viewModeInstallationPost?.slots,
    postTypeDetail?.slots,
    slotImageUrlsFromInstallationPost,
  ]);

  /** View mode: every visible slot is read-only — same disabledSlotKeys behavior as add mode when a product occupies a slot */
  const viewModeReadOnlySlotKeys = useMemo(() => {
    if (!isViewMode || !postTypeDetail?.slots?.length) return [];
    const keys: string[] = [];
    for (const s of postTypeDetail.slots) {
      if (!(visibleSlots[s.key] ?? true)) continue;
      keys.push(s.key);
    }
    return keys;
  }, [isViewMode, postTypeDetail?.slots, visibleSlots]);

  const getSlotHasSelection = useCallback(
    (slotKey: string) => {
      const product = slotProducts?.[slotKey];
      const imageUrl = slotImageUrls?.[slotKey];
      return !!(product?.product_id || imageUrl);
    },
    [slotProducts, slotImageUrls]
  );

  const validateVisibleSlots = useCallback((): string[] => {
    const errors: string[] = [];
    for (const slot of postTypeDetail?.slots ?? []) {
      // Main panel is never required for submit (custom-only / catalog orders can omit it).
      if (slot.key === "main_panel") continue;
      const isVisible = !!visibleSlots[slot.key];
      const hasSelection = getSlotHasSelection(slot.key);
      if (isVisible && !hasSelection) {
        errors.push(
          `${slot.label ?? slot.key} is visible but no product is selected. Please add a product or remove the slot.`
        );
      }
    }
    return errors;
  }, [postTypeDetail?.slots, visibleSlots, getSlotHasSelection]);

  useEffect(() => {
    if (!isViewMode && onClearSlotReady)
      onClearSlotReady({
        clearSlot,
        clearSlotByProductId,
        validateInstallationPost: validateVisibleSlots,
        applyStudioSlotImagesFromPayload,
      });
  }, [isViewMode, onClearSlotReady, clearSlot, clearSlotByProductId, validateVisibleSlots, applyStudioSlotImagesFromPayload]);

  // Clear slotProducts when post type changes (add mode only — not edit)
  useEffect(() => {
    if (isViewMode || isEditMode) return;
    setSlotProducts({});
  }, [selectedPostType?.id, isViewMode, isEditMode]);

  const editHydrationKey = useMemo(() => {
    if (!isEditMode || !editModeHydrationProducts?.length) return "";
    return editModeHydrationProducts
      .filter((p) => !p.is_accessory && (p.slot_id || p.slotKey) && p.product_id)
      .map((p) => `${p.slot_id || ""}:${p.slotKey || ""}:${p.product_id}`)
      .sort()
      .join(",");
  }, [isEditMode, editModeHydrationProducts]);

  const editHydrationAppliedRef = useRef<string | null>(null);

  // Edit mode: hydrate slotProducts from parent's existing line items once post type detail is loaded
  useEffect(() => {
    if (!isEditMode || !postTypeDetail?.slots?.length || !editHydrationKey) return;
    if (editHydrationAppliedRef.current === editHydrationKey) return;
    editHydrationAppliedRef.current = editHydrationKey;

    const slotIdToKey: Record<string, string> = {};
    for (const s of postTypeDetail.slots) {
      const id = normalizeSlotId(s._id ?? (s as { id?: string }).id);
      if (id) slotIdToKey[id] = s.key;
    }

    setSlotProducts(() => {
      const next: Record<string, LinkedProduct> = {};
      for (const p of editModeHydrationProducts) {
        if (p.is_accessory) continue;
        const slotKey = p.slotKey || slotIdToKey[String(p.slot_id || "").trim()];
        if (!slotKey || !p.product_id) continue;
        next[slotKey] = {
          product_id: p.product_id,
          name: p.name || "",
          sku: p.sku || "",
          variant_id: p.variant_id,
          images: p.images?.length
            ? p.images
            : p.image_url
              ? [{ url: p.image_url, default: true }]
              : [],
        } as LinkedProduct;
      }
      return next;
    });
  }, [
    isEditMode,
    postTypeDetail?.slots,
    editHydrationKey,
    editModeHydrationProducts,
  ]);

  const resolvedPostTypeId = useMemo(() => {
    const fromDetail = String(
      postTypeDetail?._id ?? (postTypeDetail as { id?: string } | null)?.id ?? ""
    ).trim();
    if (isEditMode) {
      return String(viewModePostTypeId || fromDetail || "").trim();
    }
    return String(selectedPostType?.id || fromDetail || "").trim();
  }, [isEditMode, viewModePostTypeId, postTypeDetail, selectedPostType?.id]);

  // Build installation_post payload and notify parent (add/edit mode) so it is included in the order payload
  useEffect(() => {
    if (isViewMode || !onInstallationPostChange) return;
    if (!postTypeDetail) {
      onInstallationPostChange(null);
      return;
    }
    if (!isEditMode && !selectedPostType) {
      onInstallationPostChange(null);
      return;
    }
    const postTypeId = isEditMode
      ? String(viewModePostTypeId || postTypeDetail._id || "").trim()
      : String(postTypeDetail._id || (selectedPostType as { id?: string }).id || "").trim();
    if (!postTypeId) {
      onInstallationPostChange(null);
      return;
    }
    const slots: InstallationPostPayload["slots"] = [];
    const slotList = postTypeDetail.slots ?? [];
    for (const slot of slotList) {
      if (!visibleSlots[slot.key]) continue;
      const product = slotProducts[slot.key];
      const imageUrl = slotImageUrls?.[slot.key];
      if (!product?.product_id && !imageUrl) continue;
      const slotId = normalizeSlotId(slot._id ?? (slot as { id?: string }).id);
      const imageId =
        product.design_id && product.preview_url
          ? product.preview_url
          : product?.images?.[0]?.url ?? imageUrl ?? "";
      slots.push({
        slot_id: slotId,
        product_id: product?.product_id ?? "",
        variant_id: product?.variant_id ?? "",
        image: imageId,
      });
    }
    onInstallationPostChange({
      post_type_id: postTypeId,
      illustration: postTypeDetail.illustration ?? "",
      slots,
    });
  }, [isViewMode, isEditMode, onInstallationPostChange, selectedPostType, postTypeDetail, slotProducts, slotImageUrls, visibleSlots, viewModePostTypeId]);

  // Fetch post type dropdown options: POST organizations/{org}/customer_side/datatable with select2: true.
  // Only called when vendor is selected (vendorId is set); e.g. in Realtor, Select Vendor = selectedInstaller.
  const fetchPostTypes = useCallback(
    async (search = "") => {
      if (!effectiveVendorId || !token) return;
      const membershipId = String(postTypeOrderContext?.membershipId || "").trim();
      const licenseType = String(postTypeOrderContext?.licenseType || "").trim();
      const orderExtra =
        postTypeOrderContext != null && membershipId && licenseType
          ? {
              membership_id: membershipId,
              license_type: licenseType,
              ...(String(postTypeOrderContext.globalUserId || "").trim()
                ? { global_user_id: String(postTypeOrderContext.globalUserId).trim() }
                : {}),
              ...(String(postTypeOrderContext.organizationId || "").trim()
                ? { organization_id: String(postTypeOrderContext.organizationId).trim() }
                : {}),
            }
          : {};
      setPostTypeLoading(true);
      try {
        const response = await vendorCustomerSideSelect2(
          effectiveVendorId,
          { page: 1, limit: 50, search, sort: { field: "name", order: "asc" }, ...orderExtra },
          token
        );
        setPostTypeOptions(response.results);
        // Auto-select first option if nothing is selected yet
        if (!selectedPostType && response.results.length > 0) {
          setSelectedPostType(response.results[0]);
        }
      } catch {
        // ignore
      } finally {
        setPostTypeLoading(false);
      }
    },
    [effectiveVendorId, token, selectedPostType, postTypeOrderContext]
  );

  // When vendor is cleared, reset post type state (add mode only)
  useEffect(() => {
    if (isViewMode || isEditMode) return;
    if (!effectiveVendorId) {
      setPostTypeOptions([]);
      setSelectedPostType(null);
      setPostTypeDetail(null);
      setPostTypeSearch("");
      setPostDropdownOpen(false);
      onPostTypeChangeRef.current?.(null);
    }
  }, [effectiveVendorId, isViewMode, isEditMode]);

  // Add Installation: no end-customer yet — clear post type list (scoped API requires membership_id + license_type)
  useEffect(() => {
    if (isViewMode || isEditMode) return;
    if (postTypeOrderContext !== null) return;
    setPostTypeOptions([]);
    setSelectedPostType(null);
    setPostTypeDetail(null);
    setPostTypeSearch("");
    setPostDropdownOpen(false);
    onPostTypeChangeRef.current?.(null);
  }, [postTypeOrderContext, isViewMode, isEditMode]);

  // When end customer changes (same flow as Signage Add Installation), drop stale post type selection before refetch
  useEffect(() => {
    if (isViewMode || isEditMode) return;
    if (postTypeOrderContext === undefined || postTypeOrderContext === null) return;
    setSelectedPostType(null);
    setPostTypeDetail(null);
    onPostTypeChangeRef.current?.(null);
  }, [
    postTypeOrderContext?.membershipId,
    postTypeOrderContext?.licenseType,
    isViewMode,
    isEditMode,
  ]);

  // Initial load: fetch post types only in add mode when vendor is selected
  useEffect(() => {
    if (isViewMode || isEditMode || !effectiveVendorId || !token) return;
    if (postTypeOrderContext === null) return;
    fetchPostTypes();
  }, [effectiveVendorId, token, isViewMode, isEditMode, postTypeOrderContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search: only when vendor is selected (add mode)
  useEffect(() => {
    if (isViewMode || isEditMode || !effectiveVendorId || !token) return;
    if (postTypeOrderContext === null) return;
    const timer = setTimeout(() => {
      fetchPostTypes(postTypeSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [postTypeSearch, effectiveVendorId, token, isViewMode, isEditMode, postTypeOrderContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPostDropdownOpen(false);
        setPostTypeSearch("");
      }
    };
    if (postDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [postDropdownOpen]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (postDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [postDropdownOpen]);

  // Post type dropdown list is loaded via vendorCustomerSideSelect2 (vendors/{vendorId}/customer_side/datatable with select2: true).
  // Design/slots come from getPostTypeById when a post type is selected (postTypeDetail).
  // No separate template library call — rely on API post type detail for slots/illustration.
  useEffect(() => {
    if (!token) {
      setPostTemplates([]);
      setTemplateImages({});
    }
  }, [token]);

  // View/edit mode: fetch post type by installation_post.post_type_id so we have slots to map slot_id -> slot key
  useEffect(() => {
    const postTypeId = String(viewModePostTypeId || "").trim();
    if (
      (!isViewMode && !isEditMode) ||
      !postTypeId ||
      postTypeId === "000000000000000000000000" ||
      !effectiveVendorId ||
      !token
    ) {
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    getPostTypeById(effectiveVendorId, postTypeId, token)
      .then((detail) => {
        if (!cancelled) setPostTypeDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setPostTypeDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [isViewMode, isEditMode, viewModePostTypeId, effectiveVendorId, token]);

  // Edit mode: lock selected post type from order (no dropdown change)
  useEffect(() => {
    if (!isEditMode || !postTypeDetail) return;
    const id = String(postTypeDetail._id || viewModePostTypeId || "").trim();
    if (!id || id === "000000000000000000000000") return;
    setSelectedPostType((prev) => {
      if (prev?.id === id) return prev;
      return { id, text: postTypeDetail.name || "Post Type" };
    });
  }, [isEditMode, postTypeDetail, viewModePostTypeId]);

  // Add mode: fetch full post type detail when a post type is selected
  useEffect(() => {
    const selectedId = String(selectedPostType?.id || "").trim();
    if (
      isViewMode ||
      isEditMode ||
      !selectedId ||
      selectedId === "000000000000000000000000" ||
      !effectiveVendorId ||
      !token
    ) {
      if (!isViewMode && !isEditMode) setPostTypeDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    getPostTypeById(effectiveVendorId, selectedId, token)
      .then((detail) => {
        if (!cancelled) setPostTypeDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setPostTypeDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [isViewMode, isEditMode, selectedPostType?.id, effectiveVendorId, token]);

  // Fetch illustration template (frame) by postTypeDetail.illustration ID — GET post-type-templates/{id}
  // Then resolve presigned URLs for SVG/thumbnail so the post design shows the frame, not just slots
  useEffect(() => {
    const illustrationId = postTypeDetail?.illustration;
    if (!illustrationId || !token) {
      setPostTemplates((prev) => (prev.length ? [] : prev));
      setTemplateImages((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    getPostTypeTemplateById(illustrationId, token)
      .then((template) => {
        if (cancelled) return;
        setPostTemplates([template]);
        const assets = template?.schema?.illustration?.assets;
        if (!assets) {
          setTemplateImages({});
          return;
        }
        const assetIds = [
          assets.thumbnail?.key,
          assets.svg?.key,
          assets.preview_png?.key,
        ].filter(Boolean) as string[];
        if (assetIds.length === 0) {
          setTemplateImages({});
          return;
        }
        getPresignedAssetUrls({ token, fileIds: assetIds })
          .then((presigned) => {
            if (cancelled) return;
            const assets = template?.schema?.illustration?.assets;
            const mapped: Record<string, { preview?: string; thumbnail?: string; svg?: string }> = {};
            if (assets) {
              mapped[template.id] = {
                preview: assets.preview_png?.key ? presigned[assets.preview_png.key]?.presigned_url : undefined,
                thumbnail: assets.thumbnail?.key ? presigned[assets.thumbnail.key]?.presigned_url : undefined,
                svg: assets.svg?.key ? presigned[assets.svg.key]?.presigned_url : undefined,
              };
            }
            setTemplateImages(mapped);
          })
          .catch(() => {});
      })
      .catch(() => {
        if (!cancelled) {
          setPostTemplates([]);
          setTemplateImages({});
        }
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [postTypeDetail?.illustration, token]);

  // NOTE: Accessory rules are fetched only when the user clicks "Browse Catalog"
  // inside ProductSelectionModal (to avoid double-calling on open + browse).

  const openPanelModal = (type: "main" | "bottom" | "accessory", slot?: PostTypeSlotEntity) => {
    setPanelModal({ open: true, type, slot });
  };
  const closePanelModal = () => {
    setPanelModal({ open: false, type: "" });
  };

  // Resolve current post type design from templates (same as PostTypes.jsx) — template SVG + slots or API slots only
  const resolvedDesign = useMemo(() => {
    const templateId =
      postTypeDetail?.illustration ?? (selectedPostType?.id ? `custom-${selectedPostType.id}` : "");
    const template = postTemplates.find((t) => t.id === templateId);
    const apiSlots = postTypeDetail?.slots ?? [];
    const mergeSlotMetadata = (slots: SlotBlueprint[] | null): SlotBlueprint[] | null => {
      if (!slots?.length) return slots;
      return slots.map((slot) => {
        const apiSlot = apiSlots.find((a: any) => a.key === slot.key);
        if (!apiSlot) return slot;
        return {
          ...slot,
          display_mode: apiSlot.display_mode,
          default_visible: apiSlot.default_visible,
          allow_remove: apiSlot.allow_remove,
          group: apiSlot.group,
          required: apiSlot.required,
        };
      });
    };
    if (template) {
      const raw = (template.schema?.illustration?.slots ?? null) as SlotBlueprint[] | null;
      return {
        illustrationSvgUrl: templateImages[templateId]?.svg ?? null,
        viewBoxDimensions: getTemplateViewBoxDimensions(template),
        customSlots: mergeSlotMetadata(raw),
      };
    }
    // No template in library (e.g. custom post type): use slots from API, normalized to SlotBlueprint
    const slots = apiSlots;
    if (slots?.length) {
      const normalized: SlotBlueprint[] = slots.map((s: any) => {
        const rect = s.zone && typeof s.zone.x === "number" ? s.zone : s.rect_norm;
        return {
          key: s.key,
          label: s.label ?? s.key,
          rect_norm: rect ?? { x: 0, y: 0, w: 0, h: 0 },
          zone: s.zone,
          ui: {},
          display_mode: s.display_mode,
          default_visible: s.default_visible,
          allow_remove: s.allow_remove,
          group: s.group,
          required: s.required,
        };
      });
      return {
        illustrationSvgUrl: null,
        viewBoxDimensions: DEFAULT_VIEWBOX,
        customSlots: normalized,
      };
    }
    return {
      illustrationSvgUrl: null,
      viewBoxDimensions: DEFAULT_VIEWBOX,
      customSlots: null,
    };
  }, [
    postTypeDetail?.illustration,
    postTypeDetail?.slots,
    selectedPostType?.id,
    postTemplates,
    templateImages,
  ]);

  // Empty panelData — slots always render as empty placeholders in this view.
  // Users select products via the ProductSelectionModal when clicking a slot.
  const emptyPanelData = {};

  return (
    <div className="row col-lg-12 mb-lg-5 card-body shadow-lg mb-3 ms-0 p-3">
      <div className="d-flex align-items-center justify-content-between mb-3 ps-0">
        <SectionHeader title="📦 Post Type & Sign Configuration" icon="" variant="success" size="md" divider />
      </div>

      {/* Top bar: Post Type + Legend (add mode only) */}
      {!isViewMode && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ maxWidth: 300, minWidth: 240 }} ref={dropdownRef}>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 600,
              color: "#64748b",
              marginBottom: 5,
              letterSpacing: 0.2,
            }}
          >
            Post Type <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            {isEditMode ? (
              <div
                style={{
                  background: "#f1f5f9",
                  color: "#334155",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontSize: 13,
                  fontWeight: 500,
                  border: "1px solid #e2e8f0",
                }}
              >
                {detailLoading && !postTypeDetail?.name
                  ? "Loading..."
                  : postTypeDetail?.name || selectedPostType?.text || "Post Type"}
              </div>
            ) : (
            <>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setPostDropdownOpen(!postDropdownOpen)}
              onKeyDown={(e) => e.key === "Enter" && setPostDropdownOpen(!postDropdownOpen)}
              style={{
                background: "linear-gradient(135deg, #334155, #1e293b)",
                color: "#fff",
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                userSelect: "none",
                boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
                transition: "box-shadow 0.15s",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {selectedPostType?.post_url && (
                  <img
                    src={selectedPostType.post_url}
                    alt={selectedPostType.text}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      objectFit: "cover",
                      background: "#0f172a",
                    }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                {postTypeLoading && !selectedPostType
                  ? "Loading..."
                  : selectedPostType?.text || "Select Post Type"}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: postDropdownOpen ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s",
                }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {postDropdownOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
                  zIndex: 50,
                  overflow: "hidden",
                }}
              >
                {/* Search input */}
                <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search post types..."
                    value={postTypeSearch}
                    onChange={(e) => setPostTypeSearch(e.target.value)}
                    style={{
                      width: "100%",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      padding: "7px 10px",
                      fontSize: 12,
                      outline: "none",
                      color: "#334155",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {/* Options list */}
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {postTypeLoading ? (
                    <div style={{ padding: "12px 14px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                      Loading...
                    </div>
                  ) : postTypeOptions.length === 0 ? (
                    <div style={{ padding: "12px 14px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                      No post types found
                    </div>
                  ) : (
                    postTypeOptions.map((option, i) => {
                      const isSelected = selectedPostType?.id === option.id;
                      const bgDefault = isSelected ? "#eff6ff" : "#f1f5f9";
                      const bgHover = isSelected ? "#dbeafe" : "#e2e8f0";
                      return (
                        <div
                          key={option.id}
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => {
                            const isNewSelection = option.id !== selectedPostType?.id;
                            setSelectedPostType(option);
                            setPostDropdownOpen(false);
                            setPostTypeSearch("");
                            if (isNewSelection) onPostTypeChange?.(option);
                          }}
                          style={{
                            padding: "10px 14px",
                            fontSize: 13,
                            color: isSelected ? "#2563eb" : "#334155",
                            fontWeight: isSelected ? 600 : 400,
                            cursor: "pointer",
                            background: bgDefault,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom:
                              i < postTypeOptions.length - 1 ? "1px solid #e2e8f0" : "none",
                            transition: "background 0.1s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = bgHover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = bgDefault;
                          }}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {option.post_url && (
                              <img
                                src={option.post_url}
                                alt={option.text}
                                style={{
                                  width: 90,
                                  height: 160,
                                  borderRadius: 0,
                                  objectFit: "cover",
                                  background: "#0f172a",
                                }}
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = "none";
                                }}
                              />
                            )}
                            <span>{option.text}</span>
                          </span>
                          {isSelected && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#2563eb"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Sign Configuration Visual — white background to match post design */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: "40px 24px 32px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
          boxShadow:
            "0 1px 4px rgba(0,0,0,0.04)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {detailLoading ? (
          <span style={{ color: "#94a3b8", fontSize: 13, zIndex: 1 }}>Loading post design...</span>
        ) : templatesLoading && postTemplates.length === 0 ? (
          <span style={{ color: "#94a3b8", fontSize: 13, zIndex: 1 }}>Loading post type designs...</span>
        ) : (
          <InteractiveSignPost
            panelSlots={postTypeDetail?.illustration ?? selectedPostType?.id ?? ""}
            panelData={emptyPanelData}
            onPanelClick={isViewMode ? () => {} : (slotKey) => {
              if (slotProducts[slotKey]) return; // Slot disabled when it has a product (including no-image); remove via Product Selection delete
              const slot = postTypeDetail?.slots?.find((s) => s.key === slotKey);
              if (slot) openPanelModal("main", slot);
            }}
            customSlots={resolvedDesign.customSlots}
            viewBoxDimensions={resolvedDesign.viewBoxDimensions}
            illustrationSvgUrl={resolvedDesign.illustrationSvgUrl}
            hoveredSlot={isViewMode ? null : hoveredSlot}
            onSlotHover={isViewMode ? undefined : setHoveredSlot}
            emptyMessage={postTypeDetail?.name ? "No slots configured" : "Select a post type"}
            slotImageUrls={slotImageUrls}
            slotNoImageKeys={slotNoImageKeys}
            disabledSlotKeys={isViewMode ? viewModeReadOnlySlotKeys : Object.keys(slotProducts)}
            visibleSlots={visibleSlots}
            isViewMode={isViewMode}
            onAddSlot={isViewMode ? undefined : handleAddSlot}
            onRemoveSlot={isViewMode ? undefined : handleRemoveSlot}
          />
        )}
      </div>

      {/* Add Accessory Button (add mode only) */}
      {!isViewMode && (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 16, gap: 10 }}>
        <button
          type="button"
          onClick={() => openPanelModal("accessory")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            background: "linear-gradient(135deg, #f8fafc, #f1f5f9)",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "9px 18px",
            fontSize: 12,
            fontWeight: 600,
            color: "#475569",
            cursor: "pointer",
            transition: "all 0.15s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#e2e8f0";
            e.currentTarget.style.color = "#1e293b";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              "linear-gradient(135deg, #f8fafc, #f1f5f9)";
            e.currentTarget.style.color = "#475569";
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          🔩 &nbsp; Add Accessory
        </button>
          {onCustomItemAdd != null && (
          <button
                                type="button"
                                className="btn btn-primary table-addbutton"
                                onClick={() => setShowCustomModal(true)}
                            >
                              Custom Item
                            </button>
          )}
        {accessoryCount > 0 && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 24,
              height: 24,
              padding: "0 6px",
              borderRadius: "50%",
              background: "#2563eb",
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {accessoryCount}
          </span>
        )}
      </div>
      )}

      {/* Product Selection Modal (add mode only) */}
      {!isViewMode && (
      <ProductSelectionModal
        open={panelModal.open}
        title={
          panelModal.type === "accessory"
            ? "🔩 Add Accessory"
            : panelModal.slot
              ? `📋 ${panelModal.slot.label || panelModal.slot.key}`
              : "Select a product"
        }
        userType={pickerUserType ?? null}
        userId={pickerUserId ?? ""}
        signageCompanyId={effectiveSignageCompanyId}
        token={token}
        slotId={panelModal.slot?._id ?? panelModal.slot?.id ?? undefined}
        postTypeId={resolvedPostTypeId || undefined}
        accessoryRulesUserId={panelModal.type === "accessory" ? pickerEndUserId ?? undefined : undefined}
        studioCustomization={studioCustomization}
        selectedStockLines={selectedStockLines}
        baselineStockLines={baselineStockLines}
        onProductSelect={(product) => {
          const slotKey = panelModal.slot?.key;
          if (slotKey) {
            // Previous flow blocked assigning the same product to multiple slots or adding
            // a product that already existed in the parent Product Selection list:
            // const currentSlotProductId = String(slotProducts[slotKey]?.product_id ?? "").trim();
            // const alreadyInAnotherSlot = Object.entries(slotProducts).some(
            //   ([k, p]) => k !== slotKey && p?.product_id === product?.product_id
            // );
            // const productId = String(product?.product_id ?? "").trim();
            // const alreadySelectedInParent =
            //   !!productId &&
            //   selectedProductIdsSet.has(productId) &&
            //   productId !== currentSlotProductId;
            // if (alreadyInAnotherSlot || alreadySelectedInParent) {
            //   onSlotDuplicateProduct?.();
            //   closePanelModal();
            //   return;
            // }
            // Store product for this slot (shows image in post design)
            setSlotProducts((prev) => ({ ...prev, [slotKey]: product }));
            // Compute backend slot id for line_items (used in view mode/work order)
            const slotId = normalizeSlotId(panelModal.slot?._id ?? (panelModal.slot as { id?: string }).id);
            // Notify parent to add product to Product Selection table with slot metadata
            onSlotProductSelect?.(product, slotKey, slotId);
          } else {
            // No slot = Add Accessory flow (Product tab from Accessory); add to Product Selection as accessory (same as slot products for line items)
            onSlotProductSelect?.(product, "accessory");
          }
          closePanelModal();
        }}
        onClose={closePanelModal}
      />
      )}

      {onCustomItemAdd != null && (
      <CustomItemModal
        show={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        onSubmit={async (data: any) => {
          await onCustomItemAdd?.(data);
          setShowCustomModal(false);
        }}
      />
      )}
    </div>
  );
};

/* Post design rendering is handled by the shared InteractiveSignPost component
   imported from ../../components/InteractiveSignPost.tsx (single source of truth). */

export default PostTypeSignConfiguration;
